import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import { useTheme } from '../context/ThemeContext';
import '../styles/theme.css';
import './JobSeekerProfile.css';

import { API as API_BASE, MEDIA_URL } from '../config';
const API = API_BASE;

// ── Modal overlay helper ──────────────────────────────────────────────────────
function Modal({ title, onClose, children }) {
  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(0,0,0,0.45)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '1rem',
      }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        style={{
          background: 'var(--surface)', borderRadius: 'var(--radius-lg)',
          boxShadow: 'var(--shadow-lg)', width: '100%', maxWidth: '540px',
          maxHeight: '90vh', overflowY: 'auto', padding: '1.75rem',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
          <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700, color: 'var(--text-primary)' }}>{title}</h3>
          <button
            onClick={onClose}
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.2rem', color: 'var(--text-muted)' }}
          >
            <i className="fas fa-times"></i>
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

// ── Field — lifted OUTSIDE parent so it never remounts on re-render ──────────
function Field({ label, field, type = 'text', options, editing, profile, editedProfile, setEditedProfile }) {
  return (
    <div className="form-group">
      <label className="form-label">{label}</label>
      {editing ? (
        <div className="rp-edit-row">
          {options ? (
            <select
              className="form-control"
              value={editedProfile[field] || ''}
              onChange={e => setEditedProfile(p => ({ ...p, [field]: e.target.value }))}
            >
              <option value="">Select...</option>
              {options.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          ) : type === 'textarea' ? (
            <textarea
              className="form-control"
              rows="4"
              value={editedProfile[field] || ''}
              onChange={e => setEditedProfile(p => ({ ...p, [field]: e.target.value }))}
            />
          ) : (
            <input
              className="form-control"
              type={type}
              value={editedProfile[field] || ''}
              onChange={e => setEditedProfile(p => ({ ...p, [field]: e.target.value }))}
            />
          )}
        </div>
      ) : (
        <div className="rp-view-row">
          <span className="rp-view-value">{profile[field] || <em style={{ color: 'var(--text-muted)' }}>Not specified</em>}</span>
        </div>
      )}
    </div>
  );
}

function JobSeekerProfile() {
  const navigate = useNavigate();
  const { isDark, toggleTheme } = useTheme();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editedProfile, setEditedProfile] = useState({});
  const [saving, setSaving] = useState(false);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [saveMsg, setSaveMsg] = useState('');
  const [userTier] = useState(localStorage.getItem('user_tier') || 'free');

  // Skills editing state
  const [skillInput, setSkillInput] = useState('');
  const [editedSkills, setEditedSkills] = useState([]);
  const [savingSkills, setSavingSkills] = useState(false);

  // Work Experience modal state
  const [showAddExp, setShowAddExp] = useState(false);
  const [expForm, setExpForm] = useState({
    job_title: '', company_name: '', location: '',
    start_date: '', end_date: '', is_current: false, description: '',
  });
  const [savingExp, setSavingExp] = useState(false);

  // Education modal state
  const [showAddEdu, setShowAddEdu] = useState(false);
  const [eduForm, setEduForm] = useState({
    institution_name: '', degree: '', field_of_study: '',
    start_date: '', end_date: '', is_current: false, grade: '',
  });
  const [savingEdu, setSavingEdu] = useState(false);

  const token = () => localStorage.getItem('token');
  const headers = () => ({ Authorization: `Bearer ${token()}`, 'Content-Type': 'application/json' });

  useEffect(() => { fetchProfile(); }, []);

  const fetchProfile = async () => {
    try {
      const res = await fetch(`${API}/job-seeker-profile/my-profile/`, { headers: headers() });
      if (res.status === 404) { navigate('/profile-setup'); return; }
      if (res.status === 401) { navigate('/login'); return; }
      const data = await res.json();
      // Normalise photo URL
      if (data.profile_photo && !data.profile_photo.startsWith('http')) {
        data.profile_photo = `${MEDIA_URL}/${data.profile_photo}`;
      }
      setProfile(data);
      setEditedProfile(data);
      setEditedSkills(Array.isArray(data.skills) ? data.skills.map(s => (typeof s === 'string' ? s : s.name)) : []);
      setPhotoPreview(data.profile_photo || null);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/job-seeker-profile/my-profile/`, {
        method: 'PUT',
        headers: headers(),
        body: JSON.stringify(editedProfile),
      });
      if (res.ok) {
        const updated = await res.json();
        if (updated.profile_photo && !updated.profile_photo.startsWith('http')) {
          updated.profile_photo = `${MEDIA_URL}/${updated.profile_photo}`;
        }
        setProfile(updated);
        setEditing(false);
        setSaveMsg('Profile updated successfully');
        setTimeout(() => setSaveMsg(''), 3000);
      }
    } catch (e) { console.error(e); }
    finally { setSaving(false); }
  };

  const handlePhotoChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setPhotoPreview(URL.createObjectURL(file));
    const form = new FormData();
    form.append('file', file);
    try {
      const res = await fetch(`${API}/job-seeker-profile/my-profile/photo/`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token()}` },
        body: form,
      });
      if (res.ok) {
        const data = await res.json();
        const url = data.photo_url?.startsWith('http')
          ? data.photo_url
          : `${MEDIA_URL}/${data.photo_url}`;
        setPhotoPreview(url);
        setProfile(p => ({ ...p, profile_photo: url }));
      }
    } catch (e) { console.error(e); }
  };

  // ── Skills handlers ────────────────────────────────────────────────────────
  const handleAddSkill = () => {
    const trimmed = skillInput.trim();
    if (!trimmed || editedSkills.includes(trimmed)) return;
    setEditedSkills([...editedSkills, trimmed]);
    setSkillInput('');
  };

  const handleRemoveSkill = (skill) => {
    setEditedSkills(editedSkills.filter(s => s !== skill));
  };

  const handleSaveSkills = async () => {
    setSavingSkills(true);
    try {
      const res = await fetch(`${API}/job-seeker-profile/my-profile/`, {
        method: 'PUT',
        headers: headers(),
        body: JSON.stringify({ skills: editedSkills }),
      });
      if (res.ok) {
        setSaveMsg('Skills updated successfully');
        setTimeout(() => setSaveMsg(''), 3000);
        await fetchProfile();
      } else {
        setSaveMsg('Failed to save skills');
        setTimeout(() => setSaveMsg(''), 3000);
      }
    } catch (e) {
      console.error(e);
      setSaveMsg('Error saving skills');
      setTimeout(() => setSaveMsg(''), 3000);
    } finally {
      setSavingSkills(false);
    }
  };

  // ── Work Experience handlers ───────────────────────────────────────────────
  const handleAddExperience = async (e) => {
    e.preventDefault();
    setSavingExp(true);
    try {
      const payload = { ...expForm };
      if (payload.is_current) delete payload.end_date;
      if (!payload.end_date) delete payload.end_date;
      const res = await fetch(`${API}/job-seeker-profile/work-experience/`, {
        method: 'POST',
        headers: headers(),
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        setShowAddExp(false);
        setExpForm({ job_title: '', company_name: '', location: '', start_date: '', end_date: '', is_current: false, description: '' });
        setSaveMsg('Work experience added');
        setTimeout(() => setSaveMsg(''), 3000);
        await fetchProfile();
      } else {
        const err = await res.json().catch(() => ({}));
        setSaveMsg(err.detail || 'Failed to add experience');
        setTimeout(() => setSaveMsg(''), 3000);
      }
    } catch (e) {
      console.error(e);
      setSaveMsg('Error adding experience');
      setTimeout(() => setSaveMsg(''), 3000);
    } finally {
      setSavingExp(false);
    }
  };

  const handleDeleteExperience = async (id) => {
    if (!window.confirm('Delete this work experience?')) return;
    try {
      const res = await fetch(`${API}/job-seeker-profile/work-experience/${id}/`, {
        method: 'DELETE',
        headers: headers(),
      });
      if (res.ok) {
        setSaveMsg('Work experience deleted');
        setTimeout(() => setSaveMsg(''), 3000);
        await fetchProfile();
      }
    } catch (e) { console.error(e); }
  };

  // ── Education handlers ─────────────────────────────────────────────────────
  const handleAddEducation = async (e) => {
    e.preventDefault();
    setSavingEdu(true);
    try {
      const payload = { ...eduForm };
      if (payload.is_current) delete payload.end_date;
      if (!payload.end_date) delete payload.end_date;
      if (!payload.grade) delete payload.grade;
      const res = await fetch(`${API}/job-seeker-profile/education/`, {
        method: 'POST',
        headers: headers(),
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        setShowAddEdu(false);
        setEduForm({ institution_name: '', degree: '', field_of_study: '', start_date: '', end_date: '', is_current: false, grade: '' });
        setSaveMsg('Education added');
        setTimeout(() => setSaveMsg(''), 3000);
        await fetchProfile();
      } else {
        const err = await res.json().catch(() => ({}));
        setSaveMsg(err.detail || 'Failed to add education');
        setTimeout(() => setSaveMsg(''), 3000);
      }
    } catch (e) {
      console.error(e);
      setSaveMsg('Error adding education');
      setTimeout(() => setSaveMsg(''), 3000);
    } finally {
      setSavingEdu(false);
    }
  };

  const handleDeleteEducation = async (id) => {
    if (!window.confirm('Delete this education entry?')) return;
    try {
      const res = await fetch(`${API}/job-seeker-profile/education/${id}/`, {
        method: 'DELETE',
        headers: headers(),
      });
      if (res.ok) {
        setSaveMsg('Education deleted');
        setTimeout(() => setSaveMsg(''), 3000);
        await fetchProfile();
      }
    } catch (e) { console.error(e); }
  };

  // Field is defined above the component — no inline definition needed

  if (loading) return (
    <div className="portal-layout">
      <Sidebar />
      <main className="portal-main">
        <div className="portal-content">
          <div className="empty-state">
            <i className="fas fa-spinner fa-spin" style={{ opacity: 1, color: 'var(--primary)' }}></i>
            <p>Loading profile...</p>
          </div>
        </div>
      </main>
    </div>
  );

  if (!profile) return null;

  const initials = profile.full_name?.charAt(0).toUpperCase() || 'U';
  // Shared props for all Field components — defined here so they're stable
  const fp = { editing, profile, editedProfile, setEditedProfile };

  return (
    <div className="portal-layout">
      <Sidebar />
      <main className="portal-main">
        <div className="portal-topbar">
          <span className="topbar-title">My Profile</span>
          <div className="topbar-actions">
            <button className="topbar-btn" onClick={toggleTheme}>
              <i className={`fas ${isDark ? 'fa-sun' : 'fa-moon'}`}></i>
            </button>
          </div>
        </div>

        <div className="portal-content">

          {saveMsg && (
            <div className="alert alert-success" style={{ marginBottom: '1.5rem' }}>
              <i className="fas fa-check-circle"></i> {saveMsg}
            </div>
          )}

          {/* Hero header */}
          <div className={`card jsp-hero jsp-hero--${userTier}`}>
            <div className="jsp-photo-wrap">
              {photoPreview
                ? <img src={photoPreview} alt={profile.full_name} className="jsp-photo-img" />
                : <div className="jsp-photo-placeholder">{initials}</div>
              }
              <label className="jsp-photo-upload" title="Upload photo">
                <i className="fas fa-camera"></i>
                <input type="file" accept="image/jpeg,image/png,image/jpg" onChange={handlePhotoChange} style={{ display: 'none' }} />
              </label>
            </div>

            <div className="jsp-hero-info">
              <h1 className="jsp-name">{profile.full_name}</h1>
              <p className="jsp-title">{profile.professional_title || <em style={{ color: 'var(--text-muted)' }}>No title set</em>}</p>
              {(profile.city || profile.province) && (
                <p className="jsp-location">
                  <i className="fas fa-map-marker-alt"></i> {[profile.city, profile.province].filter(Boolean).join(', ')}
                </p>
              )}
              <div className="jsp-hero-badges">
                <span className="badge badge-blue">
                  <i className="fas fa-chart-pie"></i> {profile.profile_completion_percentage}% Complete
                </span>
                {profile.employment_status && (
                  <span className="badge badge-green">
                    <i className="fas fa-circle"></i> {profile.employment_status}
                  </span>
                )}
              </div>
            </div>

            <div className="jsp-hero-actions">
              <div className="jsp-completion-ring">
                <svg viewBox="0 0 36 36" className="jsp-ring-svg">
                  <path className="jsp-ring-bg" d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
                  <path className="jsp-ring-fill" strokeDasharray={`${profile.profile_completion_percentage}, 100`} d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
                </svg>
                <span className="jsp-ring-pct">{profile.profile_completion_percentage}%</span>
              </div>
              <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>Profile Complete</span>
              {!editing ? (
                <button className="btn btn-primary btn-sm" style={{ marginTop: '0.75rem' }} onClick={() => setEditing(true)}>
                  <i className="fas fa-edit"></i> Edit Profile
                </button>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginTop: '0.75rem' }}>
                  <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>
                    {saving ? 'Saving...' : <><i className="fas fa-check"></i> Save</>}
                  </button>
                  <button className="btn btn-secondary btn-sm" onClick={() => { setEditedProfile(profile); setEditing(false); }}>
                    Cancel
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Professional Summary */}
          <div className="card" style={{ marginTop: '1.5rem' }}>            <div className="card-header">
              <span className="card-title"><i className="fas fa-user" style={{ color: 'var(--primary)', marginRight: '0.5rem' }}></i>Professional Summary</span>
            </div>
            <Field {...fp} label="About Me" field="bio" type="textarea" />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <Field {...fp} label="Years of Experience" field="years_of_experience" type="number" />
              <Field {...fp} label="Employment Status" field="employment_status" options={['Employed', 'Unemployed', 'Student', 'Open to opportunities']} />
            </div>
          </div>

          {/* Personal Info */}
          <div className="card" style={{ marginTop: '1.5rem' }}>
            <div className="card-header">
              <span className="card-title"><i className="fas fa-address-card" style={{ color: 'var(--primary)', marginRight: '0.5rem' }}></i>Personal Information</span>
            </div>
            <Field {...fp} label="Full Name" field="full_name" />
            <Field {...fp} label="Professional Title" field="professional_title" />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <Field {...fp} label="City" field="city" />
              <Field {...fp} label="Province" field="province" options={['Gauteng','Western Cape','KwaZulu-Natal','Eastern Cape','Free State','Limpopo','Mpumalanga','North West','Northern Cape']} />
            </div>
            <Field {...fp} label="Phone" field="phone" type="tel" />
          </div>

          {/* Skills */}
          <div className="card" style={{ marginTop: '1.5rem' }}>
            <div className="card-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span className="card-title"><i className="fas fa-code" style={{ color: 'var(--primary)', marginRight: '0.5rem' }}></i>Skills</span>
            </div>

            {/* Edit mode: input + tag list */}
            {editing && (
              <div style={{ marginBottom: '1rem' }}>
                <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem' }}>
                  <input
                    className="form-control"
                    placeholder="Add a skill…"
                    value={skillInput}
                    onChange={e => setSkillInput(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAddSkill(); } }}
                    style={{ flex: 1 }}
                  />
                  <button className="btn btn-primary btn-sm" type="button" onClick={handleAddSkill}>
                    <i className="fas fa-plus"></i> Add
                  </button>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '0.75rem' }}>
                  {editedSkills.map((skill, i) => (
                    <span
                      key={i}
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: '0.35rem',
                        background: 'var(--primary-light)', color: 'var(--primary)',
                        border: '1px solid rgba(26,86,219,0.2)', borderRadius: 'var(--radius-full)',
                        padding: '0.3rem 0.75rem', fontSize: '0.82rem', fontWeight: 600,
                      }}
                    >
                      {skill}
                      <button
                        type="button"
                        onClick={() => handleRemoveSkill(skill)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--primary)', padding: 0, lineHeight: 1, fontSize: '0.85rem' }}
                        aria-label={`Remove ${skill}`}
                      >
                        <i className="fas fa-times"></i>
                      </button>
                    </span>
                  ))}
                  {editedSkills.length === 0 && (
                    <span style={{ color: 'var(--text-muted)', fontStyle: 'italic', fontSize: '0.88rem' }}>No skills added yet</span>
                  )}
                </div>
                <button
                  className="btn btn-primary btn-sm"
                  type="button"
                  onClick={handleSaveSkills}
                  disabled={savingSkills}
                >
                  {savingSkills ? 'Saving…' : <><i className="fas fa-check"></i> Save Skills</>}
                </button>
              </div>
            )}

            {/* Read-only view */}
            {!editing && (
              profile.skills && profile.skills.length > 0 ? (
                <div className="jsp-skills-grid">
                  {profile.skills.map((skill, i) => (
                    <div key={i} className="jsp-skill-item">
                      <span className="jsp-skill-name">{typeof skill === 'string' ? skill : skill.name}</span>
                      {typeof skill !== 'string' && skill.level && (
                        <span className="badge badge-blue">{skill.level}</span>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>No skills added yet. Click Edit Profile to add skills.</p>
              )
            )}
          </div>

          {/* Work Experience */}
          <div className="card" style={{ marginTop: '1.5rem' }}>
            <div className="card-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span className="card-title"><i className="fas fa-building" style={{ color: 'var(--primary)', marginRight: '0.5rem' }}></i>Work Experience</span>
              <button className="btn btn-primary btn-sm" onClick={() => setShowAddExp(true)}>
                <i className="fas fa-plus"></i> Add Experience
              </button>
            </div>
            {profile.work_experiences && profile.work_experiences.length > 0 ? (
              <div className="jsp-timeline">
                {profile.work_experiences.map((exp) => (
                  <div key={exp.id} className="jsp-timeline-item">
                    <div className="jsp-timeline-dot"></div>
                    <div className="jsp-timeline-body" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.5rem' }}>
                      <div style={{ flex: 1 }}>
                        <div className="jsp-tl-title">{exp.job_title}</div>
                        <div className="jsp-tl-company">{exp.company_name}</div>
                        {exp.location && <div className="jsp-tl-meta"><i className="fas fa-map-marker-alt"></i> {exp.location}</div>}
                        <div className="jsp-tl-meta">
                          <i className="fas fa-calendar"></i>{' '}
                          {new Date(exp.start_date).toLocaleDateString('en-ZA', { month: 'short', year: 'numeric' })} –{' '}
                          {exp.is_current ? 'Present' : exp.end_date ? new Date(exp.end_date).toLocaleDateString('en-ZA', { month: 'short', year: 'numeric' }) : ''}
                        </div>
                        {exp.description && <p className="jsp-tl-desc">{exp.description}</p>}
                      </div>
                      <button
                        className="btn btn-sm"
                        style={{ background: 'none', border: '1px solid var(--danger, #e53e3e)', color: 'var(--danger, #e53e3e)', flexShrink: 0 }}
                        onClick={() => handleDeleteExperience(exp.id)}
                        title="Delete experience"
                      >
                        <i className="fas fa-trash"></i>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>No work experience added yet.</p>
            )}
          </div>

          {/* Add Work Experience Modal */}
          {showAddExp && (
            <Modal title="Add Work Experience" onClose={() => setShowAddExp(false)}>
              <form onSubmit={handleAddExperience}>
                <div className="form-group">
                  <label className="form-label">Job Title *</label>
                  <input className="form-control" required value={expForm.job_title} onChange={e => setExpForm({ ...expForm, job_title: e.target.value })} />
                </div>
                <div className="form-group">
                  <label className="form-label">Company Name *</label>
                  <input className="form-control" required value={expForm.company_name} onChange={e => setExpForm({ ...expForm, company_name: e.target.value })} />
                </div>
                <div className="form-group">
                  <label className="form-label">Location</label>
                  <input className="form-control" value={expForm.location} onChange={e => setExpForm({ ...expForm, location: e.target.value })} />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  <div className="form-group">
                    <label className="form-label">Start Date *</label>
                    <input className="form-control" type="date" required value={expForm.start_date} onChange={e => setExpForm({ ...expForm, start_date: e.target.value })} />
                  </div>
                  {!expForm.is_current && (
                    <div className="form-group">
                      <label className="form-label">End Date</label>
                      <input className="form-control" type="date" value={expForm.end_date} onChange={e => setExpForm({ ...expForm, end_date: e.target.value })} />
                    </div>
                  )}
                </div>
                <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <input
                    type="checkbox"
                    id="exp-is-current"
                    checked={expForm.is_current}
                    onChange={e => setExpForm({ ...expForm, is_current: e.target.checked, end_date: '' })}
                  />
                  <label htmlFor="exp-is-current" style={{ margin: 0, cursor: 'pointer' }}>I currently work here</label>
                </div>
                <div className="form-group">
                  <label className="form-label">Description</label>
                  <textarea className="form-control" rows="3" value={expForm.description} onChange={e => setExpForm({ ...expForm, description: e.target.value })} />
                </div>
                <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
                  <button type="button" className="btn btn-secondary btn-sm" onClick={() => setShowAddExp(false)}>Cancel</button>
                  <button type="submit" className="btn btn-primary btn-sm" disabled={savingExp}>
                    {savingExp ? 'Saving…' : <><i className="fas fa-plus"></i> Add Experience</>}
                  </button>
                </div>
              </form>
            </Modal>
          )}

          {/* Education */}
          <div className="card" style={{ marginTop: '1.5rem' }}>
            <div className="card-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span className="card-title"><i className="fas fa-graduation-cap" style={{ color: 'var(--primary)', marginRight: '0.5rem' }}></i>Education</span>
              <button className="btn btn-primary btn-sm" onClick={() => setShowAddEdu(true)}>
                <i className="fas fa-plus"></i> Add Education
              </button>
            </div>
            {profile.educations && profile.educations.length > 0 ? (
              <div className="jsp-timeline">
                {profile.educations.map((edu) => (
                  <div key={edu.id} className="jsp-timeline-item">
                    <div className="jsp-timeline-dot"></div>
                    <div className="jsp-timeline-body" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.5rem' }}>
                      <div style={{ flex: 1 }}>
                        <div className="jsp-tl-title">{edu.degree} in {edu.field_of_study}</div>
                        <div className="jsp-tl-company">{edu.institution_name}</div>
                        <div className="jsp-tl-meta">
                          <i className="fas fa-calendar"></i>{' '}
                          {new Date(edu.start_date).toLocaleDateString('en-ZA', { month: 'short', year: 'numeric' })} –{' '}
                          {edu.is_current ? 'Present' : edu.end_date ? new Date(edu.end_date).toLocaleDateString('en-ZA', { month: 'short', year: 'numeric' }) : ''}
                        </div>
                        {edu.grade && <div className="jsp-tl-meta"><i className="fas fa-star"></i> {edu.grade}</div>}
                      </div>
                      <button
                        className="btn btn-sm"
                        style={{ background: 'none', border: '1px solid var(--danger, #e53e3e)', color: 'var(--danger, #e53e3e)', flexShrink: 0 }}
                        onClick={() => handleDeleteEducation(edu.id)}
                        title="Delete education"
                      >
                        <i className="fas fa-trash"></i>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>No education added yet.</p>
            )}
          </div>

          {/* Add Education Modal */}
          {showAddEdu && (
            <Modal title="Add Education" onClose={() => setShowAddEdu(false)}>
              <form onSubmit={handleAddEducation}>
                <div className="form-group">
                  <label className="form-label">Institution Name *</label>
                  <input className="form-control" required value={eduForm.institution_name} onChange={e => setEduForm({ ...eduForm, institution_name: e.target.value })} />
                </div>
                <div className="form-group">
                  <label className="form-label">Degree *</label>
                  <input className="form-control" required value={eduForm.degree} onChange={e => setEduForm({ ...eduForm, degree: e.target.value })} />
                </div>
                <div className="form-group">
                  <label className="form-label">Field of Study *</label>
                  <input className="form-control" required value={eduForm.field_of_study} onChange={e => setEduForm({ ...eduForm, field_of_study: e.target.value })} />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  <div className="form-group">
                    <label className="form-label">Start Date *</label>
                    <input className="form-control" type="date" required value={eduForm.start_date} onChange={e => setEduForm({ ...eduForm, start_date: e.target.value })} />
                  </div>
                  {!eduForm.is_current && (
                    <div className="form-group">
                      <label className="form-label">End Date</label>
                      <input className="form-control" type="date" value={eduForm.end_date} onChange={e => setEduForm({ ...eduForm, end_date: e.target.value })} />
                    </div>
                  )}
                </div>
                <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <input
                    type="checkbox"
                    id="edu-is-current"
                    checked={eduForm.is_current}
                    onChange={e => setEduForm({ ...eduForm, is_current: e.target.checked, end_date: '' })}
                  />
                  <label htmlFor="edu-is-current" style={{ margin: 0, cursor: 'pointer' }}>I am currently studying here</label>
                </div>
                <div className="form-group">
                  <label className="form-label">Grade / GPA (optional)</label>
                  <input className="form-control" value={eduForm.grade} onChange={e => setEduForm({ ...eduForm, grade: e.target.value })} />
                </div>
                <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
                  <button type="button" className="btn btn-secondary btn-sm" onClick={() => setShowAddEdu(false)}>Cancel</button>
                  <button type="submit" className="btn btn-primary btn-sm" disabled={savingEdu}>
                    {savingEdu ? 'Saving…' : <><i className="fas fa-plus"></i> Add Education</>}
                  </button>
                </div>
              </form>
            </Modal>
          )}

          {/* Job Preferences */}
          <div className="card" style={{ marginTop: '1.5rem' }}>
            <div className="card-header">
              <span className="card-title"><i className="fas fa-bullseye" style={{ color: 'var(--primary)', marginRight: '0.5rem' }}></i>Job Preferences</span>
            </div>
            <Field {...fp} label="Work Mode Preference" field="work_mode_preference" options={['Remote','Hybrid','On-site','Any']} />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <Field {...fp} label="Min Salary (ZAR)" field="salary_min" type="number" />
              <Field {...fp} label="Max Salary (ZAR)" field="salary_max" type="number" />
            </div>
            <Field {...fp} label="Notice Period" field="notice_period" options={['Immediate','1 week','1 month','2 months','3 months']} />
          </div>

          {/* Social Links */}
          <div className="card" style={{ marginTop: '1.5rem' }}>
            <div className="card-header">
              <span className="card-title"><i className="fas fa-link" style={{ color: 'var(--primary)', marginRight: '0.5rem' }}></i>Social Links</span>
            </div>
            <Field {...fp} label="LinkedIn URL" field="linkedin_url" type="url" />
            <Field {...fp} label="GitHub URL" field="github_url" type="url" />
            <Field {...fp} label="Portfolio URL" field="portfolio_url" type="url" />
          </div>

        </div>
      </main>
    </div>
  );
}

export default JobSeekerProfile;
